import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AllFeatureFlags } from '@models/mmobile';
import { Observable, of } from 'rxjs';
import { catchError, share, switchMap } from 'rxjs/operators';
import { POLICY_BASE_TYPES } from '../../models/policy';
import { HealthListResponse } from '../../models/policy-health';
import { LifeListResponse, OwnershipResponse } from '../../models/policy-life';
import { Memoize } from '../../utils/memoize-decorator';
import { fromEntriesArr } from '../../utils/utils';
import { BaseService } from './base.service';
import { MMobileHelperService } from './mmobile-helper.service';

@Injectable({
  providedIn: 'root'
})
export class PoliciesBaseService {
  constructor(
    private http: HttpClient,
    private base: BaseService,
    private mmobileHelper: MMobileHelperService
  ) {}

  @Memoize
  getCarPolicies(): Observable<any> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/carPolicies`;
    return this.http.get(url, this.base.getPrivateOIMHeaders()).pipe(share());
  }

  @Memoize
  getHomeOtherPolicies(): Observable<any> {
    const aciCompaniesEnabled = this.mmobileHelper.checkIsFeatureEnabled(
      AllFeatureFlags.ACI_COMPANIES_ENABLED
    );
    const url = `${this.base.getPrivateOIMBaseUrl()}/homeOtherPolicies?ACICompaniesEnabled=${aciCompaniesEnabled}`;
    return this.http.get(url, this.base.getPrivateOIMHeaders()).pipe(share());
  }

  @Memoize
  getPensionInvestmentPolicies(): Observable<any> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/pensionInvestmentPolicies?enableLifeCycle=true`;
    return this.http.get(url, this.base.getPrivateOIMHeaders()).pipe(share());
  }

  @Memoize
  getLifePolicies(): Observable<LifeListResponse> {
    return this.getOwnerships().pipe(
      switchMap(({ life }) =>
        life
          ? this.getLifePoliciesHttp()
          : of({
              savingPolicies: [],
              lifePolicies: [],
              collectivePolicies: []
            })
      ),
      share()
    );
  }

  @Memoize
  getHealthPolicies(): Observable<HealthListResponse> {
    return this.getOwnerships().pipe(
      switchMap(({ health }) =>
        health ? this.getHealthPoliciesHttp() : of({ healthPolicies: [] })
      ),
      share()
    );
  }

  @Memoize
  private getOwnerships() {
    const url = `${this.base.getPrivateOIMBaseUrl()}/policies/ownerships`;
    const response = this.mmobileHelper.getCustomConfigData(
      'OWNERSHIP_DISABLED'
    )
      ? of(this.generateAllOwnershipsResponse())
      : this.http.get<OwnershipResponse>(url, this.base.getPrivateOIMHeaders());

    return response.pipe(
      catchError(() => of(this.generateAllOwnershipsResponse())),
      share()
    );
  }

  /** @description Check first if user has policies, dont call it directly due server capacity */
  private getLifePoliciesHttp() {
    const url = `${this.base.getPrivateOIMBaseUrl()}/lifePolicies?useNew=true`;
    return this.http.get<LifeListResponse>(
      url,
      this.base.getPrivateOIMHeaders()
    );
  }

  /** @description Check first if user has policies, dont call it directly due server capacity */
  private getHealthPoliciesHttp() {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies`;
    return this.http.get<HealthListResponse>(
      url,
      this.base.getPrivateOIMHeaders()
    );
  }

  private generateAllOwnershipsResponse(): OwnershipResponse {
    return POLICY_BASE_TYPES
      .map(value => [value, true])
      .reduce(fromEntriesArr, {});
  }
}
